import React, {
  useRef,
  useState,
  useEffect,
  RefObject,
  useCallback,
  useMemo,
} from 'react';
import { FaClipboard, FaBuilding, FaCalendar } from 'react-icons/fa';

import {
  Box,
  Flex,
  Heading,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Button,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, parseISO } from 'date-fns';
import * as Yup from 'yup';

import { useAuth } from '~/hooks/auth';
import {
  ActivityProps,
  Attendance,
} from '~/modules/activityExecution/@types/activity';
import AlertDialog from '~/shared/components/AlertDialog';
import CheckboxChakra from '~/shared/components/InputChakra/CheckboxChakra';
import api from '~/shared/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import Highlights from '../Highlights';
import InputChakra from '../InputChakra';
import SelectChakra from '../InputChakra/SelectChakra';
import Loading from '../Loading';
import ModalChakra from '../Modal/ChakraUI';

interface ModalNewPlanningProps {
  attendanceId: number;
  onDone: () => void;
  // onStart: () => void;
  isOpen: boolean;
  onClose: () => void;
  myself?: boolean;
}

interface ActivityEmployee extends ActivityProps {
  allowed_employee: boolean;
  isChecked: boolean;
}

interface PlanningData {
  activity_id: string;
  employee_id: string;
  minimum_start: string;
  duration: string;
  fatal_date: string;
  checkactivity?: string;
}

interface FormData {
  attendance_id: number;
  planning: PlanningData[];
  mode: 'queue' | 'start' | 'urgent';
  is_urgent?: number;
  start_first?: number;
}

const ModalNewPlanning: React.FC<ModalNewPlanningProps> = ({
  attendanceId,
  onDone,
  isOpen,
  onClose,
  myself = false,
}) => {
  const formRef = useRef<FormHandles>(null);
  const cancelRef = useRef(null);
  const { user } = useAuth();
  const addToast = useToast();
  const [attendance, setAttendance] = useState<Attendance>();
  const [activities, setActivities] = useState<ActivityEmployee[]>([]);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);

  const {
    isOpen: isOpenCancel,
    onOpen: onOpenCancel,
    onClose: onCloseCancel,
  } = useDisclosure();

  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const [loadingPlanning, setloadingPlanning] = useState(false);

  useEffect(() => {
    setLoadingAttendance(true);

    api
      .get<Attendance>(`attendances/${attendanceId}`)
      .then((resp) => {
        setAttendance(resp.data);
      })
      .catch((err) => {
        addToast({
          position: 'top-right',
          title: 'Erro ao obter informações do atendimento',
          description:
            err.response.data?.error ||
            'Não foi possível obter as informações do atendimento',
          status: 'error',
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingAttendance(false);
      });

    setLoadingActivities(true);
    api
      .get<ActivityEmployee[]>(`attendances/${attendanceId}/plannings`, {
        params: {
          planning_by_employee: 1,
        },
      })
      .then((resp) => {
        const { data } = resp;

        // console.log(data);

        setActivities(
          data.map((activ) => {
            return {
              ...activ,
              isChecked: activ.allowed_employee,
            };
          }),
        );
      })
      .finally(() => setLoadingActivities(false));
  }, [attendanceId, addToast]);

  const validateDuration = useCallback(
    (idx: number, currentDuration: string): boolean => {
      const defaultDuration = activities.find((act, index) => idx === index)
        ?.duration;

      if (!defaultDuration) {
        return false;
      }

      if (
        !currentDuration ||
        currentDuration === '00:00:00' ||
        currentDuration > defaultDuration
      ) {
        const formattedDurationformat = format(
          parseISO(`${currentDate} ${defaultDuration}`),
          'HH:mm',
        );

        const inputRef = `planning[${idx}].duration`;

        formRef.current?.setFieldError(
          inputRef,
          `A duração deve ser válida e não pode ser exceder ${formattedDurationformat} para esta atividade`,
        );

        return false;
      }

      return true;
    },
    [activities, currentDate],
  );

  const handleSubmit = useCallback(
    async (data: FormData): Promise<void> => {
      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          attendance_id: Yup.string().required('Id do atendimento obrigatório'),
          planning: Yup.array(
            Yup.object().shape({
              duration: Yup.string().required('Defina a duração da atividade'),
            }),
          ).required(),
        });

        const dataFiltered: PlanningData[] = data.planning?.filter(
          (act: any) => {
            return act.checkactivity?.length > 0;
          },
        );

        if (!dataFiltered.length) {
          addToast({
            position: 'top-right',
            isClosable: true,
            title: 'Nenhuma atividade foi selecionada',
            status: 'warning',
          });
          return;
        }

        const formErrors: boolean[] = [];

        // Deve-se utilizar todos os planejamentos para os índices correspoderem ao formulário
        data.planning.forEach((df, idx) => {
          if (!df.checkactivity?.length) return;

          // Validar durações
          formErrors.push(validateDuration(idx, df.duration));
        });

        if (formErrors.some((fe) => !fe)) {
          addToast({
            position: 'top-right',
            isClosable: true,
            title: `Verifique a duração das atividades`,
            description: 'Algumas atividades, excederam o limite estabelecido',
            status: 'warning',
          });

          return;
        }

        data.planning = dataFiltered;

        await schema.validate(data, {
          abortEarly: false,
        });

        setloadingPlanning(true);

        const mode = formRef.current?.getFieldValue('mode');
        // data.mode não tem o valor, apenas com o getFieldvalue, talvez por não possuir defaultValue

        const startMode = mode === 'start';

        if (mode === 'urgent' || startMode) {
          data.is_urgent = 1;

          if (startMode) {
            data.start_first = 1;
          }
        }

        const resp = await api.post('plannings', data);

        const { plannings, started_first } = resp.data;

        let msg: string;
        let desc: string;
        let statusToast: 'success' | 'warning';

        desc = '';
        statusToast = 'success';

        msg = `${plannings.length} atividade(s) registrada(s)`;

        if (startMode && !started_first) {
          msg += ' mas não foi possível iniciar automaticamente';
          desc = 'Verifique se há outra atividade em execução';
          statusToast = 'warning';
        } else if (startMode) {
          desc = 'Iniciando a primeira atividade';
        }

        addToast({
          position: 'top-right',
          isClosable: true,
          title: msg,
          status: statusToast,
          description: desc,
        });

        onClose();

        /* Independente se a atividade for iniciada automaticamente ou não, é necessário recarregar os planejamentos na página para
        exbir os novos que foram recem planejados para o funcionário. */
        onDone();

        // setTimeout(() => window.location.reload(), 3000);

        /* if (mode === 'start' && onStart) {
          const firstPlanning = plannings[0];

          onStart({
            activity_id: firstPlanning.atividade_id,
            attendance_id: firstPlanning.atendimento_id,
            created_at: firstPlanning.created_at,
            company_client_id:
          });
        } */
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          position: 'top-right',
          isClosable: true,
          title:
            err.response.data?.error ||
            'Não foi possível planejar a(s) atividade(s)',
          status: 'error',
        });
      } finally {
        setloadingPlanning(false);
      }
    },
    [addToast, onClose, onDone, validateDuration],
  );

  return (
    <>
      <ModalChakra
        title={`Atend. ${attendanceId}`}
        onClose={onOpenCancel}
        isOpen={isOpen}
        isLoading={loadingPlanning}
        modalFooter={false}
        closeOnOverlayClick={false}
      >
        {loadingAttendance || loadingActivities ? (
          <Loading />
        ) : (
          <Box>
            <Box mb={4} textAlign="center" p={2}>
              <Flex w="full" alignItems="center" mb={2}>
                <Box as={FaClipboard} size="20px" mr={4} minW="20px" />

                <Text fontWeight={700}>{attendance?.demand.name}</Text>
              </Flex>
              <Flex w="full" alignItems="center" mb={2}>
                <Box as={FaBuilding} size="20px" mr={4} minW="20px" />
                <Text fontWeight={700}>{attendance?.company_client.name}</Text>
              </Flex>
              {attendance?.competence && (
                <Flex w="full" alignItems="center">
                  <Box as={FaCalendar} size="20px" mr={4} minW="20px" />
                  <Text fontWeight={700}>{attendance?.competence}</Text>
                </Flex>
              )}

              <Alert status="info" my={4} fontSize="sm">
                <AlertIcon />
                Selecione as atividades abaixo e defina a duração daquelas que
                serão planejadas
              </Alert>
              <Form onSubmit={handleSubmit} ref={formRef}>
                {!!activities?.length &&
                  activities.map((activity, idx) => {
                    return (
                      <Box
                        key={activity.activity_id}
                        borderColor={activity.isChecked ? 'green.500' : ''}
                        bg={activity.isChecked ? 'green.50' : ''}
                        borderWidth={1}
                        rounded="sm"
                        mb={2}
                        width="full"
                        p={2}
                      >
                        <Box display="none">
                          <InputChakra type="text" name="mode" />
                          <InputChakra
                            type="hidden"
                            name="attendance_id"
                            defaultValue={attendance?.id}
                          />
                          <InputChakra
                            type="hidden"
                            name={`planning[${idx}].activity_id`}
                            defaultValue={activity.activity_id}
                          />
                          <InputChakra
                            type="hidden"
                            name={`planning[${idx}].employee_id`}
                            defaultValue={attendance?.user_id}
                          />
                          <InputChakra
                            type="hidden"
                            name={`planning[${idx}].minimum_start`}
                          />
                          <InputChakra
                            type="hidden"
                            name={`planning[${idx}].fatal_date`}
                          />
                        </Box>
                        <CheckboxChakra
                          name={`planning[${idx}].checkactivity`}
                          onChange={() => {
                            setActivities((state) =>
                              state.map((actv) => {
                                return Number(actv.activity_id) ===
                                  Number(activity.activity_id)
                                  ? {
                                      ...actv,
                                      isChecked: !actv.isChecked,
                                    }
                                  : actv;
                              }),
                            );
                          }}
                          options={[
                            {
                              id: String(activity.activity_id),
                              value: String(activity.activity_id),
                              label: `${activity.order_activity} - ${activity.name}`,
                              isDisabled: !activity.allowed_employee,
                              isChecked: activity.isChecked,
                              obsDisabled: `A atividade não permite a execução pela função de ${user.responsibility}`,
                            },
                          ]}
                          isInline
                          mb={activity.isChecked ? 4 : 0}
                        />
                        <Flex flexDir="column" textAlign="left" width="full">
                          {!myself && (
                            <Box>
                              <Text>Executante</Text>
                              <Heading size="md">{user.name}</Heading>
                            </Box>
                          )}
                          {activity.isChecked && (
                            <InputChakra
                              type="time"
                              label="Duração da ativade"
                              name={`planning[${idx}].duration`}
                              width="auto"
                              defaultValue={format(
                                parseISO(`${currentDate} ${activity.duration}`),
                                'HH:mm',
                              )}
                              mb={0}
                              isInline
                            />
                          )}
                        </Flex>
                      </Box>
                    );
                  })}
              </Form>
            </Box>
            <Flex justifyContent="flex-end">
              <Button
                variant="ghost"
                colorScheme="gray"
                mr={3}
                onClick={onOpenCancel}
              >
                Cancelar
              </Button>
              <Menu>
                <MenuButton
                  as={Button}
                  variant="solid"
                  colorScheme="blue"
                  isLoading={loadingPlanning}
                  /*  onClick={() => formRef.current?.submitForm()} */
                  disabled={
                    loadingPlanning || (!loadingPlanning && !activities.length)
                  }
                >
                  Confirmar
                </MenuButton>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      formRef.current?.setFieldValue('mode', 'start');
                      formRef.current?.submitForm();
                    }}
                    // isDisabled //verificar se há alguma atividade em execução ou não
                  >
                    Iniciar agora
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      formRef.current?.setFieldValue('mode', 'urgent');
                      formRef.current?.submitForm();
                    }}
                  >
                    Inserir na fila com prioridade
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      formRef.current?.setFieldValue('mode', 'queue');
                      formRef.current?.submitForm();
                    }}
                  >
                    Inserir na fila de atividades
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
          </Box>
        )}
      </ModalChakra>
      <AlertDialog
        title="Sair do Planejamento?"
        isOpen={isOpenCancel}
        leastDestructiveRef={cancelRef}
        onClose={onCloseCancel}
        onSubmit={() => {
          onCloseCancel();
          onClose();
        }}
        colorScheme="blue"
        submitButtonColor="red.500"
        titleButtonCancel="Não"
        titleButtonConfirm="Sim"
      >
        <Text fontSize="md">
          O atendimento já foi criado, após o cancelamento você não conseguirá
          voltar deste ponto. Para continuar o planejamento das atividades neste
          atendimento, solicite ao seu líder quando necessário
        </Text>
      </AlertDialog>
    </>
  );
};

export default ModalNewPlanning;
